import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../../../../context/authContext";
import { getPlantData, getProjectData } from "../PMSTab2/4d/api";
import { getUserTasks } from "../../../../../api/tasksApi";
import { getPlantMis, upsertPlantMis } from "../../../../../api/misApi";
import { secondAxios } from "../../../../../api/axios";

const MISContext = createContext({
  receivablesRows: [],
  setReceivablesRows: () => {},
  hoursRows: [],
  invoicesRows: [],
  setInvoicesRows: () => {},
  manhoursRows: [],
  setManhoursRows: () => {},
  displayMonths: ["jan", "feb", "mar"],
  loading: false,
  poPayablesData: [],
  poPayablesLoading: false,
});

const monthKeys = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

const getMonthKey = (dateValue) => {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  return monthKeys[date.getMonth()];
};

const getTaskCode = (task) =>
  task?.taskCode || task?.task_code || task?.code || task?.task_id;

// Prefer grouping by task "type" (e.g. subcategory name) for MIS receivables
const getTaskLabel = (task) =>
  // From userTasks shape (see staff/MyTasks*)
  task?.subCategory?.name ||
  // Possible alternate shapes
  task?.taskType ||
  task?.type ||
  task?.name ||
  // Fallbacks
  getTaskCode(task);

const getTaskDate = (task) =>
  task?.startDate || task?.actualStart || task?.targetDate || task?.endDate;

// For hours aggregation, prefer grouping by task "type" when it is
// one of the main MIS disciplines (Engineering, Fabrication),
// otherwise fall back to the generic task label.
const getHoursLabel = (task) => {
  const type = task?.type;
  const lowerType = type?.toLowerCase?.();
  if (lowerType === "engineering" || lowerType === "fabrication") {
    return type;
  }
  return getTaskLabel(task);
};

const buildMonthlyRows = (tasks) => {
  // Group by task type/label instead of individual task id/code
  const rowsByLabel = new Map();
  const taskCountByLabel = new Map(); // track total number of tasks per type
  (Array.isArray(tasks) ? tasks : []).forEach((task) => {
    const label = getTaskLabel(task);
    if (!label) return;
    const monthKey = getMonthKey(getTaskDate(task));
    const progress = Number(task?.progress ?? task?.totalProgress ?? 0);

    if (!rowsByLabel.has(label)) {
      rowsByLabel.set(label, {
        id: label,
        task: label,
        firstDate: getTaskDate(task) || "",
        jan: 0,
        feb: 0,
        mar: 0,
        apr: 0,
        may: 0,
        jun: 0,
        jul: 0,
        aug: 0,
        sep: 0,
        oct: 0,
        nov: 0,
        dec: 0,
      });
      taskCountByLabel.set(label, 0);
    }

    // Increment total task count for this type
    taskCountByLabel.set(label, taskCountByLabel.get(label) + 1);

    const row = rowsByLabel.get(label);
    if (monthKey) {
      row[monthKey] = (Number(row[monthKey]) || 0) + (progress || 0);
    }

    if (row.firstDate && getTaskDate(task)) {
      const rowDate = new Date(row.firstDate);
      const taskDate = new Date(getTaskDate(task));
      if (!Number.isNaN(taskDate.getTime()) && taskDate < rowDate) {
        row.firstDate = getTaskDate(task);
      }
    }
  });

  // Convert sums to cumulative percentages: (cumulative progress up to each month / (total tasks * 100)) * 100
  return Array.from(rowsByLabel.entries()).map(([label, row]) => {
    const totalTasks = taskCountByLabel.get(label) || 0;
    const maxProgress = totalTasks * 100;
    let cumulativeProgress = 0;
    monthKeys.forEach((key) => {
      // Add this month's progress to cumulative total
      cumulativeProgress += Number(row[key]) || 0;
      if (maxProgress > 0) {
        row[key] = (cumulativeProgress / maxProgress) * 100;
      } else {
        row[key] = 0;
      }
    });
    return row;
  });
};

// Build hours rows from tasks using actualHours field
const buildHoursRows = (tasks) => {
  // Group by task type/label (Engineering, Fabrication) and sum actualHours by month
  const rowsByLabel = new Map();
  (Array.isArray(tasks) ? tasks : []).forEach((task) => {
    const label = getHoursLabel(task);
    if (!label) return;
    const monthKey = getMonthKey(getTaskDate(task));
    const hours = Number(
      task?.actualHours ?? task?.hours ?? task?.manhours ?? 0,
    );

    if (!rowsByLabel.has(label)) {
      rowsByLabel.set(label, {
        id: label,
        task: label,
        jan: 0,
        feb: 0,
        mar: 0,
        apr: 0,
        may: 0,
        jun: 0,
        jul: 0,
        aug: 0,
        sep: 0,
        oct: 0,
        nov: 0,
        dec: 0,
      });
    }

    const row = rowsByLabel.get(label);
    if (monthKey) {
      row[monthKey] = (Number(row[monthKey]) || 0) + hours;
    }
  });

  return Array.from(rowsByLabel.values());
};

const buildManhoursRows = (monthlyRows) =>
  monthlyRows.map((row, index) => ({
    id: `${row.task}-${index}`,
    task: row.task,
    date: row.firstDate || "",
    manhours: monthKeys.reduce((sum, key) => sum + (Number(row[key]) || 0), 0),
    piping: "",
    structural: "",
    equipment: "",
    welding: "",
    fabrication: "",
  }));
// Build manpower loading rows: one row per Engineering/Fabrication task
const buildManpowerRowsFromTasks = (tasks) => {
  const normalizeDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10); // YYYY-MM-DD
  };

  return (Array.isArray(tasks) ? tasks : [])
    .filter((task) => {
      const type = task?.type?.toLowerCase?.();
      return type === "engineering" || type === "fabrication";
    })
    .map((task, index) => {
      const type = task.type.toLowerCase();
      const discipline = type.charAt(0).toUpperCase() + type.slice(1); // Engineering / Fabrication

      const startDateValue =
        task.startDate ||
        task.actualStart ||
        task.targetDate ||
        task.endDate ||
        task.createdAt;

      const endDateValue = task.endDate || task.targetDate || startDateValue;

      const description =
        task.task ||
        task.name ||
        task.subCategory?.name ||
        task.description ||
        getTaskLabel(task);

      const budget =
        Number(
          task.estimatedTime ??
            task.estimatedHours ??
            task.actualHours ??
            task.hours ??
            task.manhours ??
            0,
        ) || 0;

      return {
        id: task.id || getTaskCode(task) || `${discipline}-${index}`,
        discipline,
        baselineStart: normalizeDate(startDateValue),
        baselineFinish: normalizeDate(endDateValue),
        description: description || "",
        budgetHours: budget,
      };
    });
};

const getDisplayMonths = (rows) => {
  let latestIndex = -1;
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    monthKeys.forEach((key, index) => {
      if (Number(row[key]) > 0) {
        latestIndex = Math.max(latestIndex, index);
      }
    });
  });
  if (latestIndex === -1) return ["jan", "feb", "mar"];
  return [
    monthKeys[latestIndex],
    monthKeys[(latestIndex + 1) % monthKeys.length],
    monthKeys[(latestIndex + 2) % monthKeys.length],
  ];
};

// Dummy data adjusted so each task averages to 80% progress
// This 80 will also appear as progress in the Invoices tab
const buildDemoReceivablesRows = () => [
  {
    id: 1,
    task: "Engineering",
    jan: 75,
    feb: 78,
    mar: 82,
    apr: 80,
    may: 85,
    jun: 80,
    // Sum = 480, Avg = 80
  },
  {
    id: 2,
    task: "Procurement",
    jan: 70,
    feb: 75,
    mar: 85,
    apr: 82,
    may: 88,
    jun: 80,
    // Sum = 480, Avg = 80
  },
  {
    id: 3,
    task: "Fabrication",
    jan: 72,
    feb: 76,
    mar: 84,
    apr: 78,
    may: 90,
    jun: 80,
    // Sum = 480, Avg = 80
  },
];

const buildDemoManhoursRows = () => [
  {
    id: 1,
    task: "task-1",
    date: "2026-01-15",
    designersPiping: "12",
    designersStructural: "10",
    designersEquipment: "14",
    engineersPiping: "15",
    engineersStructural: "12",
    engineersEquipment: "18",
    weld: "20",
    fab: "19",
  },
  {
    id: 2,
    task: "task-2",
    date: "2026-02-10",
    designersPiping: "8",
    designersStructural: "6",
    designersEquipment: "10",
    engineersPiping: "12",
    engineersStructural: "9",
    engineersEquipment: "14",
    weld: "10",
    fab: "11",
  },
  {
    id: 3,
    task: "task-3",
    date: "2026-03-05",
    designersPiping: "14",
    designersStructural: "12",
    designersEquipment: "16",
    engineersPiping: "18",
    engineersStructural: "16",
    engineersEquipment: "20",
    weld: "22",
    fab: "24",
  },
];

// default structure for invoices when MIS has no data yet
// default fixed rows for invoices when MIS has no data yet
// Progress set to 80 to match dummy receivables data
const buildDemoInvoicesRows = () => [
  {
    id: 1,
    task: "Engineering",
    progress: 80,
    weightedCost: "",
  },
  {
    id: 2,
    task: "Procurement",
    progress: 80,
    weightedCost: "",
  },
  {
    id: 3,
    task: "Fabrication",
    progress: 80,
    weightedCost: "",
  },
];

const buildDemoDeviationRows = () => [
  {
    id: "1",
    category: "DCS",
    referenceSource: "Ref-001 / Source A",
    budgeted: 120,
    actual: 150,
    remarks: "Extra scope",
    actionTaken: "Approve variation",
    deviationStatus: "Open",
    responsiblePerson: "Ahmed",
  },
  {
    id: "2",
    category: "MCS",
    referenceSource: "Ref-010 / Source B",
    budgeted: 300,
    actual: 260,
    remarks: "Optimization",
    actionTaken: "Document savings",
    deviationStatus: "In Progress",
    responsiblePerson: "Khaled",
  },
  {
    id: "3",
    category: "PCS",
    referenceSource: "Ref-020 / Source C",
    budgeted: 90,
    actual: 90,
    remarks: "",
    actionTaken: "",
    deviationStatus: "Closed",
    responsiblePerson: "Omar",
  },
];

export const useMIS = () => useContext(MISContext);

export const MISProvider = ({ children }) => {
  const { plantId, projectId } = useParams();
  const { accessToken } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [receivablesRows, setReceivablesRows] = useState([]);
  const [hoursRows, setHoursRows] = useState([]);
  const [invoicesRows, setInvoicesRows] = useState([]);
  const [manhoursRows, setManhoursRows] = useState([]);
  const [displayMonths, setDisplayMonths] = useState(["jan", "feb", "mar"]);
  const [poPayablesData, setPoPayablesData] = useState([]);
  const [poPayablesLoading, setPoPayablesLoading] = useState(false);

  //////// deviatiion table states /////////

  const [deviationRows, setDeviationRows] = useState([]);
  const [deviationLoading, setDeviationLoading] = useState(false);
  const [deviationSaving, setDeviationSaving] = useState(false);

  const mapTypeToCategory = (type) => {
    const t = String(type || "").toLowerCase();
    if (t === "engineering") return "DCS";
    if (t === "fabrication") return "PCS";
    return "MCS";
  };

  const fetchDeviationRows = async (tasks) => {
    const mapped = tasks
      .map((task, idx) => {
        const category = mapTypeToCategory(task?.type);
        if (!category) return null;

        return {
          id: idx,
          category,
          referenceSource: task?.taskCode,
          actual: task?.manhours,
          budgeted: task?.estimatedTime,
          responsiblePerson: task.task_members[0],

          // remarks: task?.remarks ,
          // actionTaken: task?.actionTaken,
          // deviationStatus: task?.deviationStatus
        };
      })
      .filter(Boolean);

    setDeviationRows(mapped);
  };

  const saveDeviationRow = async (rowId, patch) => {
    // 🔁 CHANGE ENDPOINT to match your backend
    await secondAxios.put(`/mis/deviation/${rowId}`, patch);
  };

  const updateDeviationRow = async (rowId, patch) => {
    setDeviationSaving(true);
    try {
      // 1) call backend (or skip if not ready)
      // await saveDeviationRow(rowId, patch);

      // 2) update local state
      setDeviationRows((prev) =>
        prev.map((r) => (r.id === rowId ? { ...r, ...patch } : r)),
      );
    } catch (e) {
      console.error("Failed to update deviation row:", e);
    } finally {
      setDeviationSaving(false);
    }
  };

  // helper to map MIS JSON shape <-> local state
  // MIS JSON structure:
  // {
  //   invoices: {
  //     engineering: { progress, weightedCost, inv1: { date, amount }, inv2: {...}, ... },
  //     procurement: { ... },
  //     fabrication: { ... }
  //   }
  // }
  const buildInvoicesFromMis = (misData) => {
    const base = buildDemoInvoicesRows();

    const invoicesJson = misData?.data?.data?.invoices || {};

    return base.map((row, idx) => {
      const key = row.task.toLowerCase();
      const storedTask = invoicesJson[key] || {};

      const storedProgress =
        storedTask.progress !== undefined && storedTask.progress !== null
          ? Number(storedTask.progress)
          : undefined;

      const result = {
        id: idx + 1,
        task: row.task,
        // If MIS has a stored progress, use it; otherwise fall back to demo default (80)
        progress:
          storedProgress !== undefined ? storedProgress : (row.progress ?? 80),
        // Keep any stored weighted cost for backward compatibility, though UI will derive it
        weightedCost: storedTask.weightedCost ?? row.weightedCost ?? "",
      };

      // Flatten nested invN objects into invNDate / invNAmount fields on the row
      Object.entries(storedTask).forEach(([k, v]) => {
        const match = k.match(/^inv(\d+)$/);
        if (!match) return;
        const n = match[1];
        const inv = v || {};
        result[`inv${n}Date`] = inv.date ?? "";
        result[`inv${n}Amount`] = inv.amount ?? "";
      });

      return result;
    });
  };

  useEffect(() => {
    const load = async () => {
      // if (!accessToken) return;
      setLoading(true);
      setDeviationLoading(true);
      try {
        // 1) Load MIS JSON for invoices (and potentially other MIS data later)
        let misData = null;
        if (plantId) {
          misData = await getPlantMis(plantId);
        }

        // Prefer tasks from tasks API for MIS receivables
        let tasks = [];

        // Fallback to plant/project based tasks if available
        if (plantId) {
          tasks = await getPlantData(plantId, accessToken);
          console.log("tasks", tasks);
          await fetchDeviationRows(tasks);
        } else if (projectId) {
          tasks = await getProjectData(projectId, accessToken);
        }

        const monthlyRows = buildMonthlyRows(tasks);
        const taskHoursRows = buildHoursRows(tasks);
        const manpowerRows = buildManpowerRowsFromTasks(tasks);

        // Use tasks-based monthly rows for receivables
        setReceivablesRows(
          monthlyRows.length > 0 ? monthlyRows : buildDemoReceivablesRows(),
        );

        // Use tasks-based hours rows (actualHours by month)
        setHoursRows(taskHoursRows);

        // Invoices: fixed rows, progress from MIS JSON if available (no longer derived from tasks)
        setInvoicesRows(buildInvoicesFromMis(misData));
        // Manhours (Manpower Loading): one row per Engineering/Fabrication task
        setManhoursRows(
          manpowerRows.length > 0 ? manpowerRows : buildDemoManhoursRows(),
        );
        setDisplayMonths(
          monthlyRows.length > 0
            ? getDisplayMonths(monthlyRows)
            : ["jan", "feb", "mar"],
        );
      } catch (error) {
        console.error("Failed to load MIS data:", error);
        setReceivablesRows(buildDemoReceivablesRows());
        setHoursRows([]);
        setInvoicesRows(buildDemoInvoicesRows());
        setManhoursRows(buildDemoManhoursRows());
        setDisplayMonths(["jan", "feb", "mar"]);
        setDeviationRows([]);
      } finally {
        setLoading(false);
        setDeviationLoading(false);
      }
    };
    load();
  }, [accessToken, plantId, projectId]);

  // Fetch payments data for payables (per-PO payments)
  useEffect(() => {
    const fetchPlantPayments = async () => {
      if (!plantId) return;
      setPoPayablesLoading(true);
      try {
        // Get all payments for this plant; backend joins Payment -> Po
        const { data } = await secondAxios.get(`/payment/plant/${plantId}`);

        console.log("=== Fetched Payments for Payables (MISContext) ===");
        console.log("Payments:", data);

        const paymentsArray = Array.isArray(data) ? data : data ? [data] : [];

        const payablesRows = paymentsArray.map((payment, index) => {
          const po = payment.po || {};
          return {
            id: payment.id || `payment-${index}`,
            paymentDate: payment.paymentDate || "",
            amount: payment.amount || "",
            forWhom: payment.forWhom || "",
            approver: payment.approver || "",
            poNumber: po.poNumber || "",
            poId: po.id || payment.poId || "",
            remarks: payment.invoiceAmount || "",
          };
        });

        console.log("=== Final Payables Rows (from payments) ===");
        console.log(payablesRows);
        console.log("====================================");

        setPoPayablesData(payablesRows);
      } catch (error) {
        console.error("Error fetching payments for payables:", error);
        setPoPayablesData([]);
      } finally {
        setPoPayablesLoading(false);
      }
    };
    fetchPlantPayments();
  }, [plantId]);

  const value = useMemo(
    () => ({
      receivablesRows,
      setReceivablesRows,
      hoursRows,
      invoicesRows,
      setInvoicesRows,
      manhoursRows,
      setManhoursRows,
      displayMonths,
      loading,
      poPayablesData,
      poPayablesLoading,
      deviationRows,
      setDeviationRows,
      deviationLoading,
      deviationSaving,
      updateDeviationRow,
    }),
    [
      receivablesRows,
      hoursRows,
      invoicesRows,
      manhoursRows,
      displayMonths,
      loading,
      poPayablesData,
      poPayablesLoading,
      deviationRows,
      deviationLoading,
      deviationSaving,
    ],
  );

  return <MISContext.Provider value={value}>{children}</MISContext.Provider>;
};
